import { type ReactNode, useEffect, useState, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { auth } from '../services/firebase'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import type { User } from 'firebase/auth'
import { checkAdminStatus } from '../services/adminApi'
import { isStaging } from '../services/environmentStore'
import {
  APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT,
  getAppSuiteActiveClientId,
  listAppSuiteUserClients,
  setAppSuiteActiveClientId,
  type AppSuiteIAMClient,
} from '../services/appfactoryApi'
import EnvironmentSwitcher from './EnvironmentSwitcher'
import './ConsoleLayout.css'
import './EnvironmentSwitcher.css'

interface AuthenticatedLayoutProps {
  children: ReactNode
  skipAdminCheck?: boolean
}

/**
 * Unified UTM layout - single header for all apps.
 * Switching apps only changes the content below the header.
 */
export default function AuthenticatedLayout({ children, skipAdminCheck = false }: AuthenticatedLayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)
  const [accountOpen, setAccountOpen] = useState(false)
  const [appsuiteClients, setAppsuiteClients] = useState<AppSuiteIAMClient[]>([])
  const [appsuiteClientId, setAppsuiteClientId] = useState('')
  const accountRef = useRef<HTMLDivElement>(null)
  const isAppSuiteShell = location.pathname.startsWith('/appsuite')

  useEffect(() => {
    if (!auth) {
      navigate('/')
      return
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser?.email) {
        navigate('/')
        return
      }

      if (skipAdminCheck) {
        setUser(firebaseUser)
        setIsAuthenticated(true)
        setAuthLoading(false)
        return
      }

      try {
        const idToken = await firebaseUser.getIdToken()
        const adminStatus = await checkAdminStatus(firebaseUser.email, idToken)
        if (!adminStatus.isAdmin) {
          navigate('/')
          return
        }
        setUser(firebaseUser)
        setIsAuthenticated(true)
      } catch (error) {
        console.error('Auth check failed:', error)
        navigate('/')
      } finally {
        setAuthLoading(false)
      }
    })

    return () => unsubscribe()
  }, [navigate, skipAdminCheck])

  // Token 自動 refresh：登入後立即檢查，之後每 50 分鐘更新（Firebase token 約 1 小時過期）
  useEffect(() => {
    if (!user) return
    const refreshToken = () => {
      user.getIdToken(true).catch((err) => {
        console.warn('[Auth] Token refresh failed:', err?.message)
      })
    }
    refreshToken() // 立即 refresh 一次（若快過期可提前更新）
    const REFRESH_INTERVAL_MS = 50 * 60 * 1000 // 50 分鐘
    const timer = setInterval(refreshToken, REFRESH_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [user])

  const handleSignOut = async () => {
    try {
      if (auth) await signOut(auth)
      navigate('/')
    } catch (error) {
      console.error('Sign out error:', error)
    }
    setAccountOpen(false)
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isAuthenticated || !isAppSuiteShell) {
      setAppsuiteClients([])
      setAppsuiteClientId('')
      return
    }

    let disposed = false
    const syncClients = async () => {
      try {
        const list = await listAppSuiteUserClients()
        if (disposed) return
        const normalized = list
          .filter((row) => String(row.id || '').trim())
          .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)))
        setAppsuiteClients(normalized)
        const cached = getAppSuiteActiveClientId()
        const picked =
          (cached && normalized.some((row) => row.id === cached) ? cached : normalized[0]?.id) || ''
        setAppsuiteClientId(picked)
        // Always call setAppSuiteActiveClientId (even when picked === cached) so that
        // pages listening for APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT know to refetch with
        // the correct clientId, fixing the race where the page mounts before syncClients
        // completes and sends its first request without ?clientId=.
        setAppSuiteActiveClientId(picked)
      } catch {
        if (disposed) return
        setAppsuiteClients([])
        setAppsuiteClientId(getAppSuiteActiveClientId())
      }
    }

    const onClientChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ clientId?: string }>
      const next = String(customEvent.detail?.clientId || getAppSuiteActiveClientId()).trim()
      setAppsuiteClientId(next)
    }

    void syncClients()
    window.addEventListener(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, onClientChanged as EventListener)
    return () => {
      disposed = true
      window.removeEventListener(APPSUITE_ACTIVE_CLIENT_CHANGED_EVENT, onClientChanged as EventListener)
    }
  }, [isAuthenticated, isAppSuiteShell])

  if (authLoading) {
    return (
      <div className="console-layout">
        <div className="console-loading">
          <div className="loading-spinner" />
          <span>Loading...</span>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  const logoSrc = '/ultron-text-logo.svg'
  const logoAlt = 'ULTRON'
  const logoLink = isAppSuiteShell ? '/appsuite' : '/'

  const handleAppSuiteClientSwitch = (value: string) => {
    const next = String(value || '').trim()
    setAppsuiteClientId(next)
    setAppSuiteActiveClientId(next)
  }

  return (
    <div className="console-layout">
      {/* Staging Environment Indicator */}
      {isStaging() && <div className="staging-indicator-bar" />}

      {/* Unified Header - same for all UTM apps */}
      <header className="console-header">
        <div className="console-header-left">
          <Link to={logoLink} className="console-logo-link">
            <img
              src={logoSrc}
              alt={logoAlt}
              className="console-header-logo"
            />
          </Link>
        </div>
        <div className="console-header-right">
          {isAppSuiteShell && appsuiteClients.length > 0 && (
            <label className="console-client-switch" title="App Suite Client">
              <span className="material-symbols-rounded">apartment</span>
              <select
                value={appsuiteClientId}
                onChange={(event) => handleAppSuiteClientSwitch(event.target.value)}
                className="console-client-switch-select"
              >
                {appsuiteClients.map((client) => {
                  const id = String(client.id || '').trim()
                  const name = String(client.name || '').trim() || id
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  )
                })}
              </select>
            </label>
          )}
          <EnvironmentSwitcher />
          <Link to="/ultron-notify" className="console-notify-btn" title="Notifications">
            <span className="material-symbols-rounded">notifications</span>
          </Link>
          <div className="console-account-wrap" ref={accountRef}>
            <button
              type="button"
              className="console-account-btn"
              onClick={() => setAccountOpen(!accountOpen)}
              title="Account"
            >
              {user?.photoURL ? (
                <img src={user.photoURL} alt="" className="console-user-avatar" />
              ) : (
                <div className="console-user-avatar-placeholder">
                  <span className="material-symbols-rounded">person</span>
                </div>
              )}
            </button>
            {accountOpen && (
              <div className="console-account-dropdown">
                <div className="console-account-dropdown-user">
                  {user?.displayName || user?.email?.split('@')[0]}
                </div>
                <button type="button" className="console-account-logout" onClick={handleSignOut}>
                  <span className="material-symbols-rounded">logout</span>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - app-specific content switches here */}
      <main className="console-main">
        <div className="console-content">
          <div className="console-page-content utm-outlet-wrapper">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="console-footer">
        <p>ULTRON © 2026</p>
      </footer>
    </div>
  )
}
